import type { ReactElement } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import Collapsible from "../../../../common-components/collapsible/Collapsible/Collapsible";
import type { Profile } from "../../types/common";
import DepartmentsDropdown from "../DepartmentsDropdown/DepartmentsDropdown";
import ExcludedTitles from "../ExcludedTitles/ExcludedTitles";
import IncludedTitles from "../IncludedTitles/IncludedTitles";
import SenioritiesDropdown from "../SenioritiesDropdown/SenioritiesDropdown";

import EmployeeProfileHeader from "./EmployeeProfileHeader/EmployeeProfileHeader";
import {
    StyledContainer,
    StyledIncludeExcludeWrapper,
    StyledLabelRow,
    StyledLabelWrapper,
    StyledMainSection,
} from "./styles";

export type EmployeeProfileProps = {
    profile: Profile;
    departments: Profile["departments"];
    seniority: Profile["seniority"];
    /** Shared personas can only be edited by account admins; everyone else sees them as they are. */
    isReadOnly?: boolean;
    onRename(profile: Profile): void;
    onExpandToggle(id: Profile["id"]): void;
    onRemoveClick(profile: Profile): void;
    onEnabledToggle(profile: Profile): void;
    onProfileChange(propertyName: keyof Profile, profile: Profile): void;
};

const EmployeeProfile = (props: EmployeeProfileProps): ReactElement => {
    const {
        profile,
        departments,
        seniority,
        isReadOnly = false,
        onRename,
        onExpandToggle,
        onRemoveClick,
        onProfileChange,
        onEnabledToggle,
    } = props;

    function handleProfileRename(newName: string) {
        onRename({ ...profile, name: newName });
    }

    function getProfilePropertyUpdater(propertyName: keyof Omit<Profile, "name" | "isExpanded" | "isEnabled">) {
        return function updateProperty(value: (typeof profile)[typeof propertyName]) {
            onProfileChange(propertyName, {
                ...profile,
                [propertyName]: value,
            });
        };
    }

    return (
        <StyledContainer>
            <EmployeeProfileHeader
                profile={profile}
                isReadOnly={isReadOnly}
                onRemoveClick={onRemoveClick}
                onExpandToggle={onExpandToggle}
                onRename={handleProfileRename}
                onIsEnabledToggle={onEnabledToggle}
            />
            <Collapsible isActive={profile.isExpanded}>
                <StyledMainSection>
                    <StyledLabelRow>
                        <StyledLabelWrapper>
                            <Text
                                fontVariant="sectionTitle"
                                lineHeight="20px"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("si.contacts-search-profiles.label.departments")}:
                            </Text>
                        </StyledLabelWrapper>
                        <DepartmentsDropdown
                            items={departments}
                            selectedIds={profile.departments}
                            isDisabled={isReadOnly}
                            onSelect={getProfilePropertyUpdater("departments")}
                        />
                    </StyledLabelRow>
                    <StyledLabelRow>
                        <StyledLabelWrapper>
                            <Text
                                fontVariant="sectionTitle"
                                lineHeight="20px"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("si.contacts-search-profiles.label.seniority")}:
                            </Text>
                        </StyledLabelWrapper>
                        <SenioritiesDropdown
                            items={seniority}
                            selectedIds={profile.seniority}
                            isDisabled={isReadOnly}
                            onSelect={getProfilePropertyUpdater("seniority")}
                        />
                    </StyledLabelRow>
                    <div>
                        <Text
                            fontVariant="primaryItem"
                            lineHeight="20px"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("si.contacts-search-profiles.label.title")}:
                        </Text>
                        <StyledIncludeExcludeWrapper>
                            <IncludedTitles
                                selectedItems={profile.includeTitles}
                                id={`title-selection-input-include-${profile.id}`}
                                isReadOnly={isReadOnly}
                                onChange={getProfilePropertyUpdater("includeTitles")}
                            />
                            <ExcludedTitles
                                selectedItems={profile.excludeTitles}
                                id={`title-selection-input-exclude-${profile.id}`}
                                isReadOnly={isReadOnly}
                                onChange={getProfilePropertyUpdater("excludeTitles")}
                            />
                        </StyledIncludeExcludeWrapper>
                    </div>
                </StyledMainSection>
            </Collapsible>
        </StyledContainer>
    );
};

export default EmployeeProfile;
